'use client';

import React, { useState } from 'react';
import Link from 'next/link';
// lucide-react 1.x removed brand icons (Twitter, LinkedIn, GitHub).
// X is the successor icon; custom SVGs handle LinkedIn and GitHub.
import { X } from 'lucide-react';
import { LinkedInIcon, GitHubIcon } from '@/components/shared/social-icons';
import { toast } from 'sonner';
import { Logo } from '@/components/shared/logo';
import { Separator } from '@/components/ui/separator';
import { subscribeNewsletter } from '@/lib/actions/newsletter';
import { cn } from '@/lib/utils';

const COMPANY_LINKS: { label: string; href: string }[] = [
  { label: 'How it works', href: '/#process' },
  { label: 'Case studies', href: '/#cases' },
  { label: 'Services', href: '/#services' },
  { label: 'Security', href: '/#security' },
];

const RESOURCE_LINKS: { label: string; href: string }[] = [
  { label: 'FAQ', href: '/#faq' },
  { label: 'Privacy', href: '/privacy' },
  { label: 'Terms', href: '/terms' },
  { label: 'Contact', href: '/#contact' },
];

const SOCIAL: { label: string; Icon: React.ComponentType<{ className?: string }>; href: string }[] = [
  { label: 'Twitter / X', Icon: X, href: 'https://x.com/gravlontech' },
  { label: 'LinkedIn', Icon: LinkedInIcon, href: 'https://linkedin.com/company/gravlon-technologies' },
  { label: 'GitHub', Icon: GitHubIcon, href: 'https://github.com/gravlon-technologies' },
];

export function Footer() {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [pending, setPending] = useState(false);

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email || pending) return;
    setPending(true);
    const result = await subscribeNewsletter(email);
    setPending(false);
    if (result.ok) {
      setSubscribed(true);
      toast.success('You\'re subscribed. First issue lands soon.');
    } else {
      toast.error(result.error);
    }
  };

  return (
    <footer className="relative border-t border-(--color-border)">
      <div className="mx-auto max-w-(--container-max) px-6 py-16">
        {/* Four-column grid */}
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-[1.5fr_1fr_1fr_1fr]">
          {/* Col 1: Brand */}
          <div className="space-y-5">
            <Logo />
            <p className="text-[14px] text-(--color-text-muted) max-w-[28ch] leading-relaxed">
              AI agent systems for ambitious operators.
            </p>
            {/* Social icons */}
            <div className="flex items-center gap-2" aria-label="Social links">
              {SOCIAL.map(({ label, Icon, href }) => (
                <a
                  key={label}
                  href={href}
                  aria-label={label}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    'inline-flex h-8 w-8 items-center justify-center rounded-md',
                    'border border-(--color-border) text-(--color-text-muted)',
                    'transition-colors duration-[var(--duration-fast)] ease-[var(--ease-out)]',
                    'hover:border-(--color-border-hover) hover:text-(--color-accent)',
                    'focus-visible:outline-2 focus-visible:outline-(--color-accent) focus-visible:outline-offset-2',
                  )}
                >
                  <Icon aria-hidden="true" className="h-[15px] w-[15px]" />
                </a>
              ))}
            </div>
          </div>

          {/* Col 2: Company */}
          <div>
            <h3 className="mb-4 text-[13px] font-semibold tracking-wide text-(--color-text) uppercase font-mono">
              Company
            </h3>
            <ul className="space-y-3">
              {COMPANY_LINKS.map(({ label, href }) => (
                <li key={label}>
                  <Link
                    href={href}
                    className={cn(
                      'text-[14px] text-(--color-text-muted)',
                      'transition-colors duration-[var(--duration-fast)] ease-[var(--ease-out)]',
                      'hover:text-(--color-text)',
                      'focus-visible:outline-2 focus-visible:outline-(--color-accent) focus-visible:outline-offset-2 rounded-sm',
                    )}
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3: Resources */}
          <div>
            <h3 className="mb-4 text-[13px] font-semibold tracking-wide text-(--color-text) uppercase font-mono">
              Resources
            </h3>
            <ul className="space-y-3">
              {RESOURCE_LINKS.map(({ label, href }) => (
                <li key={label}>
                  <Link
                    href={href}
                    className={cn(
                      'text-[14px] text-(--color-text-muted)',
                      'transition-colors duration-[var(--duration-fast)] ease-[var(--ease-out)]',
                      'hover:text-(--color-text)',
                      'focus-visible:outline-2 focus-visible:outline-(--color-accent) focus-visible:outline-offset-2 rounded-sm',
                    )}
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 4: Newsletter */}
          <div>
            <h3 className="mb-1.5 text-[13px] font-semibold tracking-wide text-(--color-text) uppercase font-mono">
              Field notes
            </h3>
            <p className="mb-4 text-[14px] text-(--color-text-muted) leading-relaxed">
              Once-monthly. Real lessons, no fluff.
            </p>

            {subscribed ? (
              <p className="text-[14px] text-(--color-accent)">
                You&rsquo;re in. First issue lands soon.
              </p>
            ) : (
              <form
                onSubmit={handleSubscribe}
                aria-label="Newsletter signup"
                className="flex flex-col gap-2"
              >
                <label htmlFor="nl-email" className="sr-only">
                  Email address
                </label>
                <input
                  id="nl-email"
                  type="email"
                  name="email"
                  autoComplete="email"
                  required
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={cn(
                    'h-9 w-full rounded-md border border-(--color-border) bg-(--color-bg-elevated)',
                    'px-3 text-[14px] text-(--color-text) placeholder:text-(--color-text-subtle)',
                    'transition-colors duration-[var(--duration-fast)] ease-[var(--ease-out)]',
                    'focus-visible:border-(--color-accent) focus-visible:outline-none',
                    'caret-[var(--color-accent)]',
                  )}
                />
                <button
                  type="submit"
                  disabled={pending}
                  aria-busy={pending}
                  className={cn(
                    'inline-flex h-9 items-center justify-center gap-1.5 rounded-md',
                    'border border-(--color-border) bg-transparent',
                    'px-4 text-[13px] font-medium text-(--color-text-muted)',
                    'transition-colors duration-[var(--duration-fast)] ease-[var(--ease-out)]',
                    'hover:border-(--color-border-hover) hover:text-(--color-text)',
                    'focus-visible:outline-2 focus-visible:outline-(--color-accent) focus-visible:outline-offset-2',
                    'disabled:opacity-50 disabled:pointer-events-none',
                  )}
                >
                  {pending ? 'Subscribing…' : 'Subscribe →'}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Separator */}
        <Separator className="my-10 bg-(--color-border)" />

        {/* Copyright row */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="font-mono text-[12px] text-(--color-text-subtle)">
            &copy; 2026 Gravlon Technologies
          </p>
          <p className="font-mono text-[12px] text-(--color-text-subtle)">
            Made with care
          </p>
        </div>
      </div>
    </footer>
  );
}
